import React, { useEffect } from "react";
import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import confetti from "canvas-confetti";
import { HOL_HOT_SEAT_CLAP_URL } from "../../constants/holDefaultSounds";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

/** @deprecated Prefer HOL_HOT_SEAT_CLAP_URL from holDefaultSounds */
export const HOT_SEAT_CLAP_URL = HOL_HOT_SEAT_CLAP_URL;

export const resolveProfile = (profilePicture) => {
  const pp = profilePicture || "";
  if (!pp) return { imgSrc: undefined, isEmoji: false, emoji: null };
  if (pp.startsWith("emoji:")) {
    return { imgSrc: undefined, isEmoji: true, emoji: pp.replace("emoji:", "") };
  }
  if (/^https?:\/\//i.test(pp)) return { imgSrc: pp, isEmoji: false, emoji: null };
  return {
    imgSrc: `${API_BASE}${pp.startsWith("/") ? pp : `/${pp}`}`,
    isEmoji: false,
    emoji: null,
  };
};

const fireCelebrationFromTop = () => {
  const end = Date.now() + 2200;
  const colors = ["#FFD700", "#29b6f6", "#00e676", "#ffffff", "#ff6b6b", "#e040fb"];
  const frame = () => {
    confetti({
      particleCount: 10,
      angle: 270,
      spread: 90,
      startVelocity: 28,
      gravity: 0.85,
      ticks: 280,
      origin: { x: Math.random(), y: 0 },
      colors,
      scalar: 1.05,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  };
  frame();
};

/**
 * Announcement when a hot seat player is selected.
 * Auto-closes after 10s; caller may also close via onClose.
 */
const HotSeatAnnounceModal = ({
  open,
  onClose,
  displayName,
  profilePicture,
  isSelf = false,
  celebrate = false,
}) => {
  const { imgSrc, isEmoji, emoji } = resolveProfile(profilePicture);
  const name = isSelf ? "You" : displayName || "Player";
  const initial = ((displayName || name).trim()[0] || "?").toUpperCase();

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => onClose?.(), 10000);
    return () => clearTimeout(timer);
  }, [open, onClose, displayName, profilePicture]);

  useEffect(() => {
    if (!open || !celebrate) return undefined;
    fireCelebrationFromTop();
    return undefined;
  }, [open, celebrate, displayName, profilePicture]);

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#1a1a1a",
          borderRadius: 3,
          border: "1px solid rgba(255,193,7,0.35)",
          overflow: "visible",
        },
      }}
    >
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={{ position: "absolute", right: 8, top: 8, color: "rgba(255,255,255,0.7)" }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ textAlign: "center", py: 4, px: 3 }}>
        <Typography
          variant="overline"
          sx={{ letterSpacing: 2, color: "#ffca28", fontWeight: 700 }}
        >
          Hot Seat
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
          <Avatar
            src={imgSrc}
            sx={{
              width: 120,
              height: 120,
              fontSize: isEmoji ? "3.5rem" : "2.75rem",
              bgcolor: "rgba(255,193,7,0.2)",
              border: "3px solid #ffca28",
              color: "#fff",
            }}
          >
            {isEmoji ? emoji : initial}
          </Avatar>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 800, color: "#fff", wordBreak: "break-word" }}>
          {name}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: "rgba(255,255,255,0.55)" }}>
          {isSelf ? "You're taking the hot seat" : "Taking the hot seat"}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default HotSeatAnnounceModal;
